import AirDatepicker from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';

const GLOBAL_CONTAINER_ID = 'air-datepicker-global-container';

let boundGlobalContainer = null;

const bindGlobalContainerEvents = () => {
    const container = document.getElementById(GLOBAL_CONTAINER_ID);

    if (!container || container === boundGlobalContainer) {
        return;
    }

    container.addEventListener('mousedown', (event) => event.stopPropagation());
    container.addEventListener('click', (event) => event.stopPropagation());

    boundGlobalContainer = container;
};

const PHP_TO_AIR = {
    'Y': 'yyyy',
    'y': 'yy',
    'm': 'MM',
    'n': 'M',
    'd': 'dd',
    'j': 'd',
    'F': 'MMMM',
    'M': 'MMM',
};

const SHORT_MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const LONG_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const DATEPICKER_KEY = '_airDatepicker';

const parseConstraintDate = (value) => {
    if (!value) {
        return undefined;
    }

    const date = new Date(value + 'T00:00:00');

    return Number.isNaN(date.getTime()) ? undefined : date;
};

const convertPhpFormatToAir = (phpFormat) => {
    let result = '';

    for (let i = 0; i < phpFormat.length; i++) {
        const character = phpFormat[i];
        result += PHP_TO_AIR[character] ?? character;
    }

    return result;
};

const matchMonthName = (segment, names) => {
    const lower = segment.toLowerCase();

    for (let i = 0; i < names.length; i++) {
        if (lower.startsWith(names[i].toLowerCase())) {
            return { month: i + 1, length: names[i].length };
        }
    }

    return null;
};

const parseDateFromFormat = (value, phpFormat) => {
    if (!value) {
        return undefined;
    }

    const tokens = phpFormat.match(/[a-zA-Z]|[^a-zA-Z]+/g) ?? [];
    let position = 0;
    let year     = null;
    let month    = null;
    let day      = null;

    for (const token of tokens) {
        if (!/[a-zA-Z]/.test(token)) {
            position += token.length;
            continue;
        }

        const segment = value.substring(position);

        switch (token) {
            case 'Y':
                year      = parseInt(segment.substring(0, 4), 10);
                position += 4;
                break;
            case 'y':
                year      = 2000 + parseInt(segment.substring(0, 2), 10);
                position += 2;
                break;
            case 'm':
                month     = parseInt(segment.substring(0, 2), 10);
                position += 2;
                break;
            case 'n':
                {
                    const digits = segment.match(/^(\d{1,2})/);
                    month        = digits ? parseInt(digits[1], 10) : null;
                    position    += digits ? digits[1].length : 0;
                }
                break;
            case 'M':
                {
                    const result = matchMonthName(segment, SHORT_MONTHS);
                    month        = result ? result.month : null;
                    position    += result ? result.length : 0;
                }
                break;
            case 'F':
                {
                    const result = matchMonthName(segment, LONG_MONTHS);
                    month        = result ? result.month : null;
                    position    += result ? result.length : 0;
                }
                break;
            case 'd':
                day       = parseInt(segment.substring(0, 2), 10);
                position += 2;
                break;
            case 'j':
                {
                    const digits = segment.match(/^(\d{1,2})/);
                    day          = digits ? parseInt(digits[1], 10) : null;
                    position    += digits ? digits[1].length : 0;
                }
                break;
            case 'S':
                {
                    const suffix = segment.match(/^(st|nd|rd|th)/i);
                    position    += suffix ? suffix[1].length : 0;
                }
                break;
            default:
                position += 1;
                break;
        }
    }

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return undefined;
    }

    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);

    return date;
};

export const toggleDatepickerFromSuffix = (suffix) => {
    const input      = suffix.parentNode.querySelector('input[type="text"]');
    const datepicker = input?.[DATEPICKER_KEY];

    if (!datepicker) {
        return;
    }

    if (datepicker.visible) {
        datepicker.hide();
    } else {
        datepicker.show();
    }
};

export const initializeDatepicker = (container) => {
    const input = container.querySelector('input[type="text"]');

    if (!input || input[DATEPICKER_KEY]) {
        return;
    }

    const phpFormat     = input.dataset.dateFormat ?? 'Y-m-d';
    const airFormat     = convertPhpFormatToAir(phpFormat);
    const minDate       = parseConstraintDate(input.dataset.minDate);
    const maxDate       = parseConstraintDate(input.dataset.maxDate);
    const existingValue = input.value.trim();
    const selectedDates = [];
    const existingDate  = parseDateFromFormat(existingValue, phpFormat);

    if (existingDate) {
        selectedDates.push(existingDate);
    }

    input.value = '';

    input[DATEPICKER_KEY] = new AirDatepicker(input, {
        locale:     localeEn,
        dateFormat: airFormat,
        autoClose:  true,
        showEvent:  'mousedown',
        position:   'bottom left',
        minDate,
        maxDate,
        selectedDates,
        onSelect:   () => {
            input.dispatchEvent(new Event('change', { bubbles: true }));
        },
    });

    bindGlobalContainerEvents();
};
